import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useMetadata,
  useTemplate,
  useFields,
} from "../context/MetadataContext";
import LoadingSpinner, {
  PageLoading,
  SectionLoading,
  InlineLoading,
} from "../components/ui/LoadingSpinner";
import ErrorDisplay, {
  NetworkError,
  ValidationError,
} from "../components/ui/ErrorDisplay";
import { ComponentErrorBoundary } from "../components/ErrorBoundary";
import useAsyncOperation from "../hooks/useAsyncOperation";
import { trackNavigation } from "../utils/analytics";

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { initialized, isLoading, getError, templates, fields, enrichments } =
    useMetadata();
  const [demoError, setDemoError] = useState(null);
  const [showDemo, setShowDemo] = useState(false);

  // Demo async operation
  const demoAsyncOp = useAsyncOperation(
    async () => {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return { message: "Demo operation completed successfully!" };
    },
    {
      onSuccess: (data) => console.log("Demo success:", data),
      onError: (error) => console.error("Demo error:", error),
    }
  );

  const triggerDemoError = () => {
    setDemoError(new Error("This is a demo error to showcase error handling"));
  };

  const clearDemoError = () => {
    setDemoError(null);
  };

  // Navigation handlers for Quick Actions
  const handleNewProject = () => {
    navigate("/new-project");
  };

  const handleNewDeal = () => {
    navigate("/new-deal");
  };

  const handleNewValuation = () => {
    navigate("/new-valuation");
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6 lg:p-8">
        <h1 className="text-3xl lg:text-4xl font-bold mb-4">
          Welcome to Landworth Deals
        </h1>
        <p className="text-lg lg:text-xl opacity-90">
          Your comprehensive Real Estate Deal Management System
        </p>

        {/* Metadata Status Indicator */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isLoading("initialize") ? (
              <InlineLoading
                text="Loading metadata..."
                size="sm"
                color="white"
              />
            ) : initialized ? (
              <div className="flex items-center space-x-2 text-green-100">
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm">Metadata system ready</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-yellow-100">
                <svg
                  className="h-4 w-4 animate-pulse"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm">Initializing metadata...</span>
              </div>
            )}
          </div>

          {/* Import Data Button */}
          <button
            disabled={true}
            className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1.5 rounded-lg transition-colors opacity-70 cursor-not-allowed flex items-center space-x-2"
            title="Import functionality coming soon"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
              />
            </svg>
            <span>Import Data</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Valuations Card */}
        <div
          onClick={() => navigate("/valuations")}
          className="card hover:shadow-md transition-shadow cursor-pointer hover:bg-gray-50 relative"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">💰</span>
              <h2 className="text-xl font-semibold">Valuations</h2>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNewValuation();
              }}
              className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors font-medium shadow-sm"
            >
              + New
            </button>
          </div>
          <p className="text-gray-600 mb-4">Property valuation and analysis</p>
          <div className="text-3xl font-bold text-green-600 mb-1">5</div>
          <div className="text-sm text-gray-500">Recent Valuations</div>
        </div>

        {/* Projects Card */}
        <div
          onClick={() => navigate("/projects")}
          className="card hover:shadow-md transition-shadow cursor-pointer hover:bg-gray-50 relative"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">📊</span>
              <h2 className="text-xl font-semibold">Projects</h2>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNewProject();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors font-medium shadow-sm"
            >
              + New
            </button>
          </div>
          <p className="text-gray-600 mb-4">
            Manage your real estate development projects
          </p>
          <div className="text-3xl font-bold text-blue-600 mb-1">4</div>
          <div className="text-sm text-gray-500">Active Projects</div>
        </div>

        {/* Deals Card */}
        <div
          onClick={() => navigate("/deals")}
          className="card hover:shadow-md transition-shadow cursor-pointer hover:bg-gray-50 relative"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">🤝</span>
              <h2 className="text-xl font-semibold">Deals</h2>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNewDeal();
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors font-medium shadow-sm"
            >
              + New
            </button>
          </div>
          <p className="text-gray-600 mb-4">
            Track property deals and transactions
          </p>
          <div className="text-3xl font-bold text-purple-600 mb-1">5</div>
          <div className="text-sm text-gray-500">Open Deals</div>
        </div>

        {/* Automations Card */}
        <div
          onClick={() => {
            trackNavigation(location.pathname, "/automations");
            navigate("/automations");
          }}
          className="card hover:shadow-md transition-shadow cursor-pointer hover:bg-gray-50 relative"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">🤖</span>
              <h2 className="text-xl font-semibold">Automations</h2>
            </div>
            {/* No "New" button for now */}
          </div>
          <p className="text-gray-600 mb-4">
            Automated workflows and processes
          </p>
          <div className="text-3xl font-bold text-indigo-600 mb-1">2</div>
          <div className="text-sm text-gray-500">Active</div>
        </div>
      </div>

      {/* Metadata System Demo */}
      <div className="card">
        <h2 className="text-2xl font-semibold mb-6">Metadata System Status</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <MetadataStatusCard
            title="Templates"
            data={templates}
            icon="📋"
            description="Form layout configurations"
          />
          <MetadataStatusCard
            title="Fields"
            data={fields}
            icon="📝"
            description="Field definitions and validation"
          />
          <MetadataStatusCard
            title="Enrichments"
            data={enrichments}
            icon="🔗"
            description="External API integrations"
          />
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">
            Demo Loading & Error States
          </h3>

          <div className="flex flex-wrap gap-4 mb-6">
            <button
              onClick={() => demoAsyncOp.execute()}
              disabled={demoAsyncOp.loading}
              className="btn-primary disabled:opacity-50"
            >
              {demoAsyncOp.loading ? (
                <div className="flex items-center space-x-2">
                  <LoadingSpinner size="xs" color="white" />
                  <span>Loading...</span>
                </div>
              ) : (
                "Test Async Operation"
              )}
            </button>

            <button onClick={triggerDemoError} className="btn-secondary">
              Trigger Demo Error
            </button>

            <button
              onClick={() => setShowDemo(!showDemo)}
              className="btn-secondary"
            >
              {showDemo ? "Hide" : "Show"} Demo Components
            </button>
          </div>

          {/* Demo Error Display */}
          {demoError && (
            <div className="mb-4">
              <ErrorDisplay
                error={demoError}
                onRetry={clearDemoError}
                retryText="Clear Error"
                className="mb-4"
              />
            </div>
          )}

          {/* Demo Async Result */}
          {demoAsyncOp.data && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">✅ {demoAsyncOp.data.message}</p>
            </div>
          )}

          {/* Demo Components */}
          {showDemo && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-medium mb-2">Loading States</h4>
                  <div className="space-y-2">
                    <InlineLoading text="Inline loading..." />
                    <div className="relative h-16 bg-gray-50 rounded">
                      <SectionLoading text="Section loading..." />
                    </div>
                  </div>
                </div>

                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-medium mb-2">Error Types</h4>
                  <div className="space-y-2">
                    <NetworkError
                      onRetry={() => console.log("Network retry")}
                    />
                    <ValidationError
                      errors={["Field is required", "Invalid email format"]}
                      onRetry={() => console.log("Validation retry")}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* UI Components Demo Section */}
      <div className="mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            🎨 UI Components Integration Demo
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Sidebar Features */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-800">📋 Enhanced Sidebar</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✅ Accordion navigation with collapsible sections</li>
                <li>✅ Smart mobile responsiveness</li>
                <li>✅ User profile integration</li>
                <li>✅ Badge notifications on nav items</li>
                <li>✅ Auto-close on mobile navigation</li>
              </ul>
            </div>

            {/* Header Features */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-800">
                📱 Scroll-Aware Header
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✅ Auto-hide on scroll down</li>
                <li>✅ Background blur when scrolled</li>
                <li>✅ Responsive search functionality</li>
                <li>✅ Notification badge indicators</li>
                <li>✅ User profile dropdown</li>
              </ul>
            </div>

            {/* Navigation Features */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-800"> Navigation Logic</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✅ React Router integration</li>
                <li>✅ Active state management</li>
                <li>✅ Breadcrumb support</li>
                <li>✅ Keyboard accessibility</li>
                <li>✅ Touch-friendly mobile UX</li>
              </ul>
            </div>
          </div>

          {/* Interactive Demo Instructions */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">
              🚀 Try the Interactive Features
            </h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p>
                • <strong>Scroll down</strong> to see the header auto-hide and
                background blur
              </p>
              <p>
                • <strong>Toggle the sidebar</strong> (☰ button) to see
                accordion navigation
              </p>
              <p>
                • <strong>Click navigation items</strong> to see active state
                management
              </p>
              <p>
                • <strong>Try the search</strong> in the header (functional
                placeholder)
              </p>
              <p>
                • <strong>View on mobile</strong> for responsive behavior
              </p>
            </div>
          </div>

          {/* Component Architecture Info */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">
              🏗️ Component Architecture
            </h4>
            <div className="text-sm text-gray-700">
              <p className="mb-2">
                The new UI system is built with modular, reusable components:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <strong>Core Components:</strong>
                  <ul className="ml-4 mt-1 space-y-0.5">
                    <li>
                      • <code className="text-blue-600">Sidebar</code> - Main
                      navigation container
                    </li>
                    <li>
                      • <code className="text-blue-600">Accordion</code> -
                      Collapsible content sections
                    </li>
                    <li>
                      • <code className="text-blue-600">ScrollAwareHeader</code>{" "}
                      - Smart header component
                    </li>
                  </ul>
                </div>
                <div>
                  <strong>Integration Features:</strong>
                  <ul className="ml-4 mt-1 space-y-0.5">
                    <li>• Error boundaries for robustness</li>
                    <li>• Accessibility (ARIA) support</li>
                    <li>• Smooth animations & transitions</li>
                    <li>• TypeScript-ready interfaces</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Task Progress Section */}
      <div className="mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            📈 Development Progress
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <h4 className="font-medium text-green-900">
                  Task 1: Setup Project Repository
                </h4>
                <p className="text-sm text-green-700">
                  Complete project setup with Vite, React, Tailwind CSS
                </p>
              </div>
              <div className="text-green-600 font-bold">✅ COMPLETE</div>
            </div>

            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <h4 className="font-medium text-green-900">
                  Task 2: Implement Metadata Loading Service
                </h4>
                <p className="text-sm text-green-700">
                  Advanced metadata system with validation and React context
                </p>
              </div>
              <div className="text-green-600 font-bold">✅ COMPLETE</div>
            </div>

            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <h4 className="font-medium text-green-900">
                  Task 3: Build Core UI Components
                </h4>
                <p className="text-sm text-green-700">
                  Sidebar, Accordion, and ScrollAwareHeader integration
                </p>
              </div>
              <div className="text-green-600 font-bold">✅ COMPLETE</div>
            </div>

            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div>
                <h4 className="font-medium text-blue-900">
                  Task 4: Dynamic Form System
                </h4>
                <p className="text-sm text-blue-700">
                  Coming next: Field rendering, validation, and enrichment
                </p>
              </div>
              <div className="text-blue-600 font-bold">📋 PENDING</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Overview */}
      <div className="mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            🚀 RE Platform v4 MVP Features
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-medium text-gray-900">
                  Project Management
                </h4>
                <p className="text-sm text-gray-600">
                  Create, track, and manage real estate projects with
                  comprehensive metadata
                </p>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-medium text-gray-900">Deal Processing</h4>
                <p className="text-sm text-gray-600">
                  Streamlined deal workflow with automated validation and
                  enrichment
                </p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-medium text-gray-900">Valuation Tools</h4>
                <p className="text-sm text-gray-600">
                  Advanced property valuation with external data integration
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border-l-4 border-yellow-500 pl-4">
                <h4 className="font-medium text-gray-900">Dynamic Forms</h4>
                <p className="text-sm text-gray-600">
                  Configurable form system with 25+ field types and conditional
                  logic
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-medium text-gray-900">Data Enrichment</h4>
                <p className="text-sm text-gray-600">
                  Automatic data enhancement via Companies House and other APIs
                </p>
              </div>

              <div className="border-l-4 border-indigo-500 pl-4">
                <h4 className="font-medium text-gray-900">
                  Analytics & Reporting
                </h4>
                <p className="text-sm text-gray-600">
                  Comprehensive dashboards and customizable report generation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Technical Architecture */}
      <div className="mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            🏗️ Technical Architecture
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium text-gray-800">Frontend Stack</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• React 18 with modern hooks</li>
                <li>• Vite for development & build</li>
                <li>• Tailwind CSS for styling</li>
                <li>• React Router for navigation</li>
                <li>• Heroicons for consistent iconography</li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-gray-800">UI Components</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Modular component architecture</li>
                <li>• Accessibility-first design</li>
                <li>• Error boundary protection</li>
                <li>• Responsive mobile support</li>
                <li>• Theme-consistent styling</li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-gray-800">Data Management</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• React Context for global state</li>
                <li>• Schema-based validation</li>
                <li>• Intelligent caching layer</li>
                <li>• Error handling & recovery</li>
                <li>• Performance optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Test Content */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            📜 Scroll Behavior Demo
          </h3>
          <p className="text-gray-600 mb-4">
            This section provides additional content to demonstrate the
            scroll-aware header functionality. Keep scrolling to see the header
            behavior in action!
          </p>

          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="p-4 bg-white/70 rounded-lg">
                <h4 className="font-medium text-gray-800">
                  Demo Content Block {i + 1}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  This is placeholder content to make the page scrollable. As
                  you scroll down, notice how the header automatically hides and
                  shows based on your scroll direction. The background also
                  becomes more opaque with a blur effect for better readability.
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="text-center py-8 text-gray-500">
        <p>
          🎉 You've reached the bottom! Scroll back up to see the header
          reappear.
        </p>
        <p className="text-sm mt-2">RE Platform v4 MVP - Task 3 Complete</p>
      </div>
    </div>
  );
}

// Helper component for metadata status cards
function MetadataStatusCard({ title, data, icon, description }) {
  const itemCount = Object.keys(data || {}).length;
  const hasData = itemCount > 0;

  return (
    <ComponentErrorBoundary componentName={`${title} Status Card`}>
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <span className="text-2xl">{icon}</span>
          <h3 className="font-semibold">{title}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-3">{description}</p>
        <div className="flex items-center justify-between">
          <div
            className={`text-2xl font-bold ${hasData ? "text-green-600" : "text-gray-400"}`}
          >
            {itemCount}
          </div>
          <div
            className={`text-xs px-2 py-1 rounded-full ${
              hasData
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {hasData ? "Loaded" : "Empty"}
          </div>
        </div>
      </div>
    </ComponentErrorBoundary>
  );
}

export default Home;
